import React, { useState } from 'react';
import { apiCall } from '../../utils/apiUtils';
import { showToast } from '../../utils/toastUtils';
import { useAuth } from '../Auth/AuthContext';

const OCRUploadModal = ({ isOpen, onClose, orderId, onSuccess }) => {
    const { user, logout } = useAuth();
    const [file, setFile] = useState(null);
    const [csvFile, setCsvFile] = useState(null);
    const [bypassOcr, setBypassOcr] = useState(false);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState('select'); // select, uploading, processing

    if (!isOpen) return null;

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) {
            showToast.error("Please select a PDF file.");
            return;
        }

        setLoading(true);
        setStep('token');

        try {
            // Step 1: Get Token from OMS
            const tokenResponse = await apiCall(
                `/api/orders/${orderId}/ocr-request`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        bypass_ocr: bypassOcr,
                        // We could parse CSV here to send expected data, 
                        // but for now we'll send the CSV file itself in Step 2.
                        // If Bypass is ON, we might need the data now to bake into the token?
                        // The Architecture says Frontend uploads CSV to OCR Backend.
                        // So we pass 'bypass_ocr' flag, OCR Backend reads CSV.
                    })
                },
                localStorage.getItem('token'),
                logout
            );

            const { upload_url, token } = tokenResponse;

            setStep('uploading');

            // Step 2: Direct Upload to OCR Server
            const formData = new FormData();
            formData.append("file", file);
            if (csvFile) {
                formData.append("csv", csvFile);
            }
            formData.append("token", token);

            // We use fetch directly here because apiCall might add OMS Authorization headers
            // which we don't want for the External Server (we use the Token in body/form)
            const uploadResponse = await fetch(upload_url, {
                method: 'POST',
                body: formData,
                // Do NOT set Content-Type header when sending FormData, browser does it with boundary
            });

            if (!uploadResponse.ok) {
                throw new Error("Upload to OCR Server failed");
            }

            setStep('processing');
            showToast.success("Upload successful! Processing has started.");

            // Close after a brief delay or let user close
            setTimeout(() => {
                onSuccess();
                onClose();
            }, 2000);

        } catch (error) {
            console.error("OCR Upload Error:", error);
            showToast.error(error.message || "Failed to upload document");
            setStep('select');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Upload Order Document</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {step === 'select' && (
                    <form onSubmit={handleUpload} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                PDF Document
                            </label>
                            <input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => setFile(e.target.files[0])}
                                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                CSV Data (Optional / For E-Stamp)
                            </label>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={(e) => setCsvFile(e.target.files[0])}
                                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                            />
                        </div>

                        {/* Admin Bypass Option - Only visible for Admin role */}
                        {user?.role === 'admin' && (
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="bypass"
                                    checked={bypassOcr}
                                    onChange={(e) => setBypassOcr(e.target.checked)}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <label htmlFor="bypass" className="ml-2 block text-sm text-gray-900">
                                    Admin Bypass (Skip OCR)
                                </label>
                            </div>
                        )}

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={!file || loading}
                                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {loading ? 'Starting...' : 'Upload & Process'}
                            </button>
                        </div>
                    </form>
                )}

                {(step === 'uploading' || step === 'processing') && (
                    <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                        <p className="text-gray-900 font-medium">
                            {step === 'token' ? 'Authorizing...' :
                                step === 'uploading' ? 'Uploading to OCR Server...' : 'Processing Started!'}
                        </p>
                        <p className="text-gray-500 text-sm mt-2">Please wait...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OCRUploadModal;
